import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { DivIcon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import L from 'leaflet';
import { Mill, StateType } from '../../lib/types';
import { MAP_CONFIG, MILL_ACCENT_COLOR, MILL_ICON_SIZE, CLUSTER_ICON_SIZE } from '../../lib/mapConfig';
import { useMapControls } from '../../hooks/useMapControls';
import { useMapReady } from '../../hooks/useMapReady';
import { MapZoomControl } from '../shared/MapZoomControl';
import { BasemapSwitchControl } from '../shared/BasemapSwitchControl';
import { BasemapZoomClamp } from '../shared/BasemapZoomClamp';
import { WaterMask } from '../shared/WaterMask';
import { AgbMapLegend } from '../shared/AgbMapLegend';
import { useBasemap } from '../../context/BasemapContext';
import { Basemap } from '../../lib/types';
import {
  readSavedExplorerMapView,
  writeSavedExplorerMapView,
} from '../../lib/explorerMapView';

interface MapViewProps {
  mills: Mill[];
  selectedState: StateType | null;
  selectedMill: Mill | null;

  onMillNavigate: (mill: Mill) => void;
}

// Create mill marker icon - matching Figma design
const createMillIcon = (isSelected: boolean) => {
  const borderWidth = isSelected ? 6 : 5;
  const size = MILL_ICON_SIZE;

  // Animation class is on the inner div, NOT the DivIcon wrapper,
  // because Leaflet uses CSS transform on the wrapper for positioning.
  const htmlIcon = `
    <div class="${isSelected ? 'marker-selected' : ''}" style="
      width: ${size}px;
      height: ${size}px;
      background: ${MILL_ACCENT_COLOR};
      border: ${borderWidth}px solid ${isSelected ? '#46ce85' : '#4A5C1F'};
      border-radius: 50%;
      cursor: pointer;
      box-shadow: ${isSelected ? '0 0 8px #46ce85' : 'none'};
    "></div>
  `;

  return new DivIcon({
    html: htmlIcon,
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Component to handle map interactions and clustering
const MapController: React.FC<{
  mills: Mill[];
  selectedState: StateType | null;
  selectedMill: Mill | null;
  basemap: Basemap;
  onMillNavigate: (mill: Mill) => void;
}> = ({ mills, selectedState, selectedMill, basemap, onMillNavigate }) => {
  const map = useMap();
  const ready = useMapReady(map);
  const { flyToState, flyToMill } = useMapControls(map);
  const lastFitBasemapRef = useRef<string | null>(null);
  const location = useLocation();
  // Captured at first render — `location.state.fromDetail` is set when the
  // user clicked "Back" on DetailView. We freeze it on a ref so the
  // first-mount restore branch below doesn't re-fire if the user later
  // interacts with the map and the location object changes shape.
  const fromDetailRef = useRef(
    (location.state as { fromDetail?: boolean } | null)?.fromDetail === true,
  );
  // Tracks whether the first-mount fly/restore has run. Prevents the
  // combined effect below from re-running its initial branch on
  // subsequent state/mill changes; in-session changes follow the
  // default flyTo path.
  const initialFlyDoneRef = useRef(false);

  // Fit-to-all-mills on initial mount AND on basemap change. The
  // MAP_CONFIG.defaultCenter (CONUS centroid) ignored the panel offset, so the
  // western mills sat off-screen left on wide desktops. Re-fitting on basemap
  // change matters because different basemaps clamp the zoom range differently
  // (Stadia minZoom=4, AGB minZoom=5) and the prior fit's zoom may no longer
  // fit all mills at the new range. Skipped while a state/mill is selected —
  // those flights take precedence.
  useEffect(() => {
    if (!ready) return;
    if (selectedMill || selectedState || mills.length === 0) return;
    if (lastFitBasemapRef.current === basemap.id) return;
    lastFitBasemapRef.current = basemap.id;
    const bounds = L.latLngBounds(mills.map((m) => [m.latitude, m.longitude]));
    map.fitBounds(bounds, { padding: [60, 60], animate: false });
  }, [ready, basemap.id, map, mills, selectedMill, selectedState]);

  // Persist the user's current pan/zoom to sessionStorage on every
  // moveend/zoomend so navigating Back from DetailView can restore the
  // regional context they were exploring (e.g. a cluster-zoomed view
  // of the West Coast mills) instead of flying all the way in to the
  // just-selected mill. Debounced because Leaflet emits move/zoom-end
  // pairs during animated transitions.
  useEffect(() => {
    if (!map) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const save = () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        const c = map.getCenter();
        writeSavedExplorerMapView({
          lat: c.lat,
          lng: c.lng,
          zoom: map.getZoom(),
        });
      }, 250);
    };
    map.on('moveend', save);
    map.on('zoomend', save);
    return () => {
      map.off('moveend', save);
      map.off('zoomend', save);
      if (timer !== null) clearTimeout(timer);
    };
  }, [map]);

  // Map-fly effect. Two branches:
  //   1) First-mount AND arrived from DetailView with a saved view →
  //      restore the saved center+zoom instantly. The user lands on the
  //      regional context they were exploring before drilling into the
  //      mill. Falls through to the default branch if no saved view.
  //   2) Default — first mount without a saved view, OR any in-session
  //      selection change: flyToMill / flyToState as before.
  useEffect(() => {
    if (!ready) return;
    if (!initialFlyDoneRef.current) {
      initialFlyDoneRef.current = true;
      if (fromDetailRef.current) {
        const saved = readSavedExplorerMapView();
        if (saved) {
          map.setView([saved.lat, saved.lng], saved.zoom, { animate: false });
          return;
        }
      }
    }
    if (selectedMill) {
      flyToMill(selectedMill);
    } else if (selectedState) {
      flyToState(selectedState);
    }
  }, [ready, selectedState, selectedMill]);

  // Set up marker clustering (excludes selected mill).
  // Gated on `ready` so the initial cluster pass uses real map bounds, not
  // the 0×0 snapshot Leaflet sees during its pre-layout initialization.
  useEffect(() => {
    if (!ready) return;
    const mcg = L.markerClusterGroup({
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        const size = CLUSTER_ICON_SIZE;
        return L.divIcon({
          html: `
            <div class="cluster-marker">
              <div class="cluster-marker-inner">
                <span class="cluster-count">${count}</span>
              </div>
            </div>
          `,
          className: 'custom-cluster-icon',
          iconSize: L.point(size, size),
          iconAnchor: [size / 2, size / 2],
        });
      },
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      spiderfyOnMaxZoom: true,
      // removeOutsideVisibleBounds causes phantom clusters at the global
      // centroid when the container narrows (e.g. DevTools docked) and all
      // markers fall outside the visible bounds.
      removeOutsideVisibleBounds: false,
      animate: true,
      // Wide zooms need a big radius so the 5 west-coast + 5 southeast mills
      // each merge into a single cluster. Tighten at z≥7 for regional views.
      maxClusterRadius: (zoom) => (zoom <= 6 ? 160 : 60),
    });

    mills.forEach((mill) => {
      // Skip selected mill — rendered separately below
      if (selectedMill?.id === mill.id) return;

      const marker = L.marker([mill.latitude, mill.longitude], {
        icon: createMillIcon(false),
        title: mill.display_name,
      });
      marker.bindTooltip(mill.display_name, { direction: 'top', offset: [0, -10] });
      marker.on('click', () => onMillNavigate(mill));
      mcg.addLayer(marker);
    });

    map.addLayer(mcg);
    return () => { map.removeLayer(mcg); };
  }, [ready, map, mills, selectedMill, onMillNavigate]);

  // Render selected mill as a standalone marker (not clustered)
  useEffect(() => {
    if (!ready || !selectedMill) return;

    const marker = L.marker([selectedMill.latitude, selectedMill.longitude], {
      icon: createMillIcon(true),
      title: selectedMill.display_name,
      zIndexOffset: 1000,
    });
    marker.bindTooltip(selectedMill.display_name, { direction: 'top', offset: [0, -10] });
    marker.on('click', () => onMillNavigate(selectedMill));
    marker.addTo(map);

    return () => { map.removeLayer(marker); };
  }, [ready, map, selectedMill, onMillNavigate]);

  return null;
};

export const MapView: React.FC<MapViewProps> = ({
  mills,
  selectedState,
  selectedMill,
  onMillNavigate,
}) => {
  const { basemap, openPicker } = useBasemap();

  return (
    <div className="absolute inset-0 md:relative md:inset-auto md:w-full md:h-full md:flex-1">
      <MapContainer
        center={MAP_CONFIG.defaultCenter}
        zoom={MAP_CONFIG.defaultZoom}
        minZoom={MAP_CONFIG.minZoom}
        maxZoom={MAP_CONFIG.maxZoom}
        maxBounds={MAP_CONFIG.conusBounds}
        maxBoundsViscosity={0.7}
        className="h-full w-full"
        zoomControl={false}
      >
        {/* Base map layer — key forces Leaflet to remount on basemap switch */}
        <TileLayer
          key={basemap.id}
          url={basemap.url}
          attribution={basemap.attribution}
          opacity={basemap.opacity ?? 1}
          minZoom={basemap.minZoom}
          maxZoom={basemap.maxZoom}
          maxNativeZoom={basemap.maxNativeZoom}
          bounds={basemap.kind === 'thematic' ? MAP_CONFIG.thematicBasemapBounds : undefined}
        />
        {basemap.kind === 'thematic' && <WaterMask />}
        <BasemapZoomClamp
          basemap={basemap}
          fallbackMinZoom={MAP_CONFIG.minZoom}
          fallbackMaxZoom={MAP_CONFIG.maxZoom}
        />

        {/* Map controller with clustering */}
        <MapController
          mills={mills}
          selectedState={selectedState}
          selectedMill={selectedMill}
          basemap={basemap}
          onMillNavigate={onMillNavigate}
        />

        {/* Map controls — basemap switcher prepended to zoom buttons */}
        <MapZoomControl>
          <BasemapSwitchControl onClick={openPicker} />
        </MapZoomControl>
      </MapContainer>

      {/* Gist-earth gradient legend for the AGB basemap — only shown
          when AGB is active; Stadia satellite doesn't need one. Rescale
          [0, 500] Mg/ha matches the precompute script's vmin/vmax. */}
      {basemap.id === 'earthmover_agb_100m' && (
        <div className="absolute bottom-[var(--mobile-peek-clearance)] left-4 z-[500]">
          <AgbMapLegend rescale={[0, 500]} />
        </div>
      )}
    </div>
  );
};
